import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Product, ProductCategory } from '@/mocks/products';
import { productCategories } from '@/mocks/products';

const categoryKeyMap: Record<string, string> = {
  shoes: 'categories_shoes',
  hoodies: 'categories_hoodies',
  tshirts: 'categories_tshirts',
  jackets: 'categories_jackets',
  pants: 'categories_pants',
  headwear: 'categories_headwear',
  sets: 'categories_sets',
  underwear: 'categories_underwear',
  jersey: 'categories_jersey',
  accessories: 'categories_accessories',
  bags: 'categories_bags',
  belts: 'categories_belts',
  perfumes: 'categories_perfumes',
};

export default function ProductCatalog() {
  const { t } = useTranslation();

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            {t('products_title', 'Browse Products')}
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            {t(
              'products_subtitle',
              'Browse our curated collection of products from trusted sellers. Click any item to view details and purchase.'
            )}
          </p>
        </div>

        {productCategories.map((category) => (
          <CategorySection key={category.id} category={category} />
        ))}
      </div>
    </section>
  );
}

function CategorySection({ category }: { category: ProductCategory }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 260;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const categoryName = t(categoryKeyMap[category.id] || category.id, category.name);

  return (
    <div className="mb-10 md:mb-12">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg md:text-xl font-bold text-foreground-900">
          {categoryName}
        </h3>
        <a
          href={category.link}
          target="_blank"
          rel="noopener nofollow"
          className="text-sm text-primary-500 hover:text-primary-600 transition-colors font-medium whitespace-nowrap"
        >
          {t('products_view_all', 'View All')}
          <i className="ri-arrow-right-s-line ml-1 inline-block" />
        </a>
      </div>
      <div className="relative">
        <button
          type="button"
          onClick={() => scroll('left')}
          className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-white rounded-full shadow-md items-center justify-center border border-background-200 hover:bg-background-50 transition-colors cursor-pointer"
          aria-label="Scroll left"
        >
          <i className="ri-arrow-left-s-line text-lg text-foreground-700" />
        </button>
        <div
          ref={scrollRef}
          className="flex gap-3 md:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 scrollbar-hide"
          data-product-shop
        >
          {category.products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
        <button
          type="button"
          onClick={() => scroll('right')}
          className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-white rounded-full shadow-md items-center justify-center border border-background-200 hover:bg-background-50 transition-colors cursor-pointer"
          aria-label="Scroll right"
        >
          <i className="ri-arrow-right-s-line text-lg text-foreground-700" />
        </button>
      </div>
    </div>
  );
}

function ProductCard({ product }: { product: Product }) {
  return (
    <a
      href={`https://kaspreadsheet.com/goods-${product.id}.html`}
      target="_blank"
      rel="noopener nofollow"
      className="block min-w-[170px] md:min-w-[220px] snap-start bg-white rounded-lg border border-background-200 overflow-hidden hover:shadow-md transition-all duration-300 group cursor-pointer"
    >
      <div className="aspect-square overflow-hidden bg-background-100">
        <img
          src={product.img}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              'https://placehold.co/300x300/f3f4f6/9ca3af?text=Product';
          }}
        />
      </div>
      <div className="p-3">
        <h4 className="text-xs md:text-sm font-medium text-foreground-800 line-clamp-2 leading-tight min-h-[2.5rem]">
          {product.name}
        </h4>
        <p className="text-sm text-primary-600 font-semibold mt-1.5">
          {product.price}
        </p>
      </div>
    </a>
  );
}